// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { Alert, Grid, useTheme } from '@mui/material';

import Spacer from '@components/Utils/Spacer';

import { AppointmentManagementConfig, ReportingBusinessHours, ReportingMove } from '../types';
import { useReporting } from '../providers/ReportingProvider';
import {
  buildAppointmentUsage,
  buildDriverActivity,
  buildMovesByHour,
} from '../utils/reportingMetrics';
import { formatNumber, formatPercent, formatRatio } from '../utils/reportingFormatters';
import { getChartColors } from '../utils/reportingChartColors';
import MetricCard from './MetricCard';
import ChartCard from './ChartCard';
import DashboardUnavailableNotice from './DashboardUnavailableNotice';
import {
  CountBarChart,
  HorizontalCountBarChart,
  ScheduledVsCapacityChart,
  UtilizationByDayChart,
} from './ReportingCharts';

// TYPES ---------------------------------------------------------------- //

interface AppointmentUsageTabProps {
  moves: ReportingMove[];
  businessHours: ReportingBusinessHours[];
  appointmentConfig?: AppointmentManagementConfig;
  start: string;
  end: string;
  loading: boolean;
}

// COMPONENT ---------------------------------------------------------------- //

const AppointmentUsageTab = ({
  moves,
  businessHours,
  appointmentConfig,
  start,
  end,
  loading,
}: AppointmentUsageTabProps) => {
  const theme = useTheme();
  const colors = getChartColors(theme);
  const { isModuleVisible } = useReporting();
  const metricVisible = (key: string) => isModuleVisible('appointments', 'metrics', key);
  const chartVisible = (key: string) => isModuleVisible('appointments', 'charts', key);

  const usage = React.useMemo(
    () => buildAppointmentUsage({ moves, businessHours, config: appointmentConfig, start, end }),
    [moves, businessHours, appointmentConfig, start, end]
  );

  const movesByHour = React.useMemo(() => buildMovesByHour(moves), [moves]);
  const driverActivity = React.useMemo(() => buildDriverActivity(moves), [moves]);

  const hasMoves = moves.length > 0;
  const capacityAvailable = usage.hasBusinessHours && usage.totalCapacity > 0;

  return (
    <>
    {/* METRIC CARDS - separate container so cards never stretch to chart row height */}
    <Grid container spacing={2}>
      {/* DISCLAIMER */}

      <Grid size={{ xs: 12 }}>
        <Alert severity='info'>
          Appointment utilization is estimated from booked appointments, configured driver count, business hours,
          average lane duration, and move buffer. It does not use appointment algorithm slot output because slot output
          is not currently logged.
        </Alert>
      </Grid>

      {!usage.hasBusinessHours ? (
        <Grid size={{ xs: 12 }}>
          <DashboardUnavailableNotice
            severity='warning'
            message='Appointment usage needs store business hours to estimate capacity. Scheduled, completed, and canceled counts are still shown below.'
          />
        </Grid>
      ) : null}

      {/* METRIC CARDS */}

      {metricVisible('est_utilization') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label='Estimated Utilization'
            value={formatPercent(usage.estimatedUtilization)}
            subtitle={
              capacityAvailable
                ? `${formatNumber(usage.totalScheduled)} booked appointments of ~${formatNumber(usage.totalCapacity)} estimated slots.`
                : 'No estimated capacity for this range.'
            }
            tooltip='Booked appointments (non-canceled moves with a pickup time in the date range) divided by estimated slot capacity. Capacity is estimated from business hours, configured drivers, average move duration, and move buffer.'
            tone={usage.estimatedUtilization != null && usage.estimatedUtilization >= 0.8 ? 'warning' : 'default'}
            loading={loading}
          />
        </Grid>
      ) : null}
      {metricVisible('upcoming_appts') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label='Upcoming Appointments'
            value={formatNumber(usage.scheduledMoves)}
            subtitle='Booked moves with a pickup time still in the future.'
            tooltip='Non-canceled moves in the date range with a pickup time that has not happened yet. A subset of booked appointments.'
            loading={loading}
          />
        </Grid>
      ) : null}
      {metricVisible('completed_appts') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label='Completed Appointments'
            value={formatNumber(usage.completedAppointments)}
            tooltip='Moves in the date range marked delivery successful.'
            tone='success'
            loading={loading}
          />
        </Grid>
      ) : null}
      {metricVisible('canceled_appts') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label='Canceled Appointments'
            value={formatNumber(usage.canceledAppointments)}
            tooltip='Moves in the date range with any cancel status.'
            tone='error'
            loading={loading}
          />
        </Grid>
      ) : null}

      {metricVisible('configured_drivers') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard label='Configured Drivers' value={formatNumber(usage.configuredDrivers)} loading={loading} />
        </Grid>
      ) : null}
      {metricVisible('avg_moves_per_driver') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label='Avg Moves Per Driver'
            value={formatRatio(usage.avgMovesPerDriver)}
            subtitle='Non-canceled moves per configured driver.'
            loading={loading}
          />
        </Grid>
      ) : null}
      {metricVisible('unused_slots') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label='Unused Estimated Slots'
            value={capacityAvailable ? formatNumber(usage.unusedSlots) : '-'}
            subtitle={capacityAvailable ? 'Estimated slots without a booked appointment.' : 'Needs business hours.'}
            loading={loading}
          />
        </Grid>
      ) : null}
      {metricVisible('avg_move_duration') ? (
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label='Avg Move Duration'
            value={`${Math.round(usage.avgMoveDurationMinutes)} min`}
            subtitle={
              usage.usedFallbackDuration
                ? 'Fallback estimate. No completed lane durations in range.'
                : 'From completed lane durations in range.'
            }
            loading={loading}
          />
        </Grid>
      ) : null}
    </Grid>

    <Spacer size={2} />

    {/* CHARTS */}
    <Grid container spacing={2}>
      {chartVisible('daily_utilization') ? (
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title='Daily Estimated Utilization'
            subtitle='Booked appointments vs estimated capacity per day'
            empty={!capacityAvailable}
            emptyMessage='No estimated capacity for this date range.'
          >
            <UtilizationByDayChart data={usage.days} colors={colors} />
          </ChartCard>
        </Grid>
      ) : null}
      {chartVisible('booked_vs_capacity') ? (
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title='Booked vs Estimated Capacity'
            subtitle='Daily booked appointments compared to estimated capacity'
            empty={!capacityAvailable && !hasMoves}
            emptyMessage='No capacity or booked appointments for this date range.'
          >
            <ScheduledVsCapacityChart data={usage.days} colors={colors} />
          </ChartCard>
        </Grid>
      ) : null}

      {chartVisible('appt_volume_by_hour') ? (
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard title='Appointment Volume by Hour' subtitle='Based on pickup time' empty={!hasMoves}>
            <CountBarChart data={movesByHour} color={colors.pending} />
          </ChartCard>
        </Grid>
      ) : null}
      {chartVisible('driver_activity') ? (
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title='Driver Activity'
            subtitle='Top drivers by move count'
            empty={driverActivity.length === 0}
            emptyMessage='No driver activity for this date range.'
          >
            <HorizontalCountBarChart data={driverActivity} color={colors.validated} />
          </ChartCard>
        </Grid>
      ) : null}
    </Grid>
    </>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default AppointmentUsageTab;
